import React, { useCallback, useMemo, useState } from 'react';
import { checkEnvironment } from '@saviynt/common';
import {
  Button,
  ButtonIcon,
  Chip,
  Icon,
  Menu,
  Typography,
} from '@saviynt/design-system';
import { useSelector } from 'react-redux';

import { USER_REQUEST_CATEGORY } from '../../components/UserManagement/constants';
import Header from '../../components/UserManagement/Header/Header';
import Identities from '../../components/UserManagement/Identities/Identities';
import { type Form } from '../../components/UserManagement/types';
import sharedMessages from '../../components/UserManagement/UsersList/messages';
import useFormApiEnabled from '../../hooks/use-form-api-enabled';
import useInfiniteForms from '../../hooks/use-infinite-forms';
import { type RootState } from '../../services/types';
import { useDebounce } from '../../utils';
import { DEBOUNCE_DELAY } from '../../utils/constants';
import { useInvertedLocalize, useLocalize } from '../../utils/localization';

import localMessages from './messages';

import '../../components/UserManagement/UsersList/UsersSortableList/UsersSortableList.css';

const invertedMessages = { ...localMessages, ...sharedMessages };

interface EnvConfig {
  GATEWAY_API_URL?: string;
}

function UserManagement() {
  const [searchFormKeyword, setSearchFormKeyword] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const debouncedValue = useDebounce(searchFormKeyword, DEBOUNCE_DELAY);
  const localize = useLocalize();
  const l = useInvertedLocalize(invertedMessages);
  const { isFormApiEnabled } = useFormApiEnabled();

  const sideMenu = useSelector((state: RootState) => state.app.sideMenu);

  // Dynamically derive nav items from the left nav API data in Redux
  const navItems = useMemo(() => {
    const homeNode = sideMenu?.find((node) => node.id === 'home');

    if (!homeNode || !('subMenu' in homeNode)) {
      return [];
    }

    const userMgmtNode = homeNode.subMenu?.find(
      (node) => node.id === 'user-management'
    );

    if (!userMgmtNode?.subMenu) {
      return [];
    }

    return userMgmtNode.subMenu
      .filter((item) => item.link && item.id !== 'user-management-v2')
      .map((item) => ({
        id: item.id,
        link: item.link ?? '',
        label: localize({
          id: `gen2.leftnav.label.${item.id}`,
          defaultMessage: item.id,
        }),
      }));
  }, [sideMenu, localize]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteForms(
      {
        category: USER_REQUEST_CATEGORY.CREATE,
        searchText: debouncedValue,
        formCategory: '',
        formTags: '',
        urlSlug: '',
        description: '',
        size: 20,
        sortBy: 'id',
        sortOrder: 'desc',
        status: ['PUBLISHED'],
        createUser: [],
        updateUser: [],
      },
      { enabled: isFormApiEnabled }
    );

  const allForms = useMemo(
    () =>
      data?.pages.flatMap((page) => page.data).filter((form) => form.auth) ??
      [],
    [data]
  );

  const navigateToForm = useCallback((form: Form) => {
    const env = checkEnvironment() as EnvConfig;

    window.location.href = `${env.GATEWAY_API_URL ?? ''}/forms/${
      form.customFormUid
    }/${form.urlSlug}`;
    setIsMenuOpen(false);
  }, []);

  const onSearchTermChange = useCallback((searchTerm: string) => {
    setSearchFormKeyword(searchTerm);
  }, []);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

      if (
        scrollHeight - scrollTop - clientHeight < 50 &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage().catch(() => {});
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  );

  const formMenuItems = useMemo(
    () => (
      <div className='UserManagement-actionsScrollList' onScroll={handleScroll}>
        {navItems.map(({ id, link, label }) => (
          <div key={id} className='UserManagement-formItem'>
            <Typography
              kind='body2'
              desktop='body2-d'
              mobile='body2-d'
              color='--color-foreground-secondary-bold'
              lineLimit={1}>
              {label}
            </Typography>
            <Chip label={l('Old')} isInteractionDisabled size='small' />
            <ButtonIcon
              size='small'
              kind='ghost'
              icon={<Icon kind='ArrowRight' />}
              ariaLabel={l('Open {formName}', { formName: label })}
              onClick={() => {
                window.location.href = link;
                setIsMenuOpen(false);
              }}
            />
          </div>
        ))}
        {isFormApiEnabled &&
          allForms.map((form: Form) => (
            <div key={form.id} className='UserManagement-formItem'>
              <Typography
                kind='body2'
                desktop='body2-d'
                mobile='body2-d'
                color='--color-foreground-secondary-bold'
                lineLimit={1}
                tooltipConfigs={{ isPortalEnabled: true }}>
                {form.name ?? l('Untitled Form')}
              </Typography>
              <ButtonIcon
                size='small'
                kind='ghost'
                icon={<Icon kind='ArrowRight' />}
                ariaLabel={l('Open {formName}', {
                  formName: form.name ?? l('Untitled Form'),
                })}
                onClick={() => navigateToForm(form)}
              />
            </div>
          ))}
      </div>
    ),
    [navItems, allForms, navigateToForm, handleScroll, isFormApiEnabled, l]
  );

  const actionsMenu = (
    <Menu
      trigger={
        <Button kind='outlined' rightIcon={<Icon kind='ChevronDown' />}>
          {l('Actions')}
        </Button>
      }
      size='medium'
      minOptionsForSearch={0}
      onSearchTermChange={onSearchTermChange}
      isMenuExpanded={isMenuOpen}
      setIsExpanded={setIsMenuOpen}
      isPortalEnabled
      classNames={{ dropdownClassName: 'UserManagement-formsMenu' }}
      CustomMenuItems={formMenuItems}
    />
  );

  return <Identities header={<Header />} actionsMenu={actionsMenu} />;
}

export default UserManagement;
